import React, { useMemo } from 'react';
import dayjs from 'dayjs';
import { Clock } from 'lucide-react';
import { MonthlySummary } from '../../services/attendanceApi';

const WEEK = ['MON', 'TUE', 'WED', 'THU', 'FRI', 'SAT', 'SUN'];

interface Cell { day: number; label: string; cls: string }

// Build a Monday-aligned month grid from the summary's per-day records.
// Letter code per day: P = present (day / unspecified shift), N = present night shift, A = absent.
const buildCalendar = (month: string, summary: MonthlySummary | null): (Cell | null)[] => {
  const first = dayjs(`${month}-01`);
  const daysInMonth = summary?.daysInMonth || first.daysInMonth();
  const byDate = new Map<string, { present: boolean; shifts?: string[] }>();
  (summary?.days || []).forEach((d) => byDate.set(d.date, { present: d.present, shifts: d.shifts }));

  const lead = (first.day() + 6) % 7; // 0 = Monday
  const cells: (Cell | null)[] = Array.from({ length: lead }, () => null);
  const today = dayjs();
  for (let d = 1; d <= daysInMonth; d++) {
    const date = first.date(d);
    const rec = byDate.get(date.format('YYYY-MM-DD'));
    let label = '';
    let cls = 'bg-gray-50 border-gray-100 text-gray-300'; // future / no data
    if (rec?.present) {
      // Present either way; the letter distinguishes the shift.
      // N = night shift, P = present (day / unspecified shift).
      const night = (rec.shifts || []).includes('NIGHT');
      const day = (rec.shifts || []).includes('DAY');
      if (night) { label = 'N'; cls = 'bg-indigo-50 border-indigo-200 text-indigo-700'; }
      else if (day) { label = 'P'; cls = 'bg-blue-50 border-blue-200 text-blue-700'; }
      else { label = 'P'; cls = 'bg-green-50 border-green-200 text-green-700'; }
    } else if (!date.isAfter(today, 'day')) {
      label = 'A'; cls = 'bg-white border-gray-200 text-gray-400'; // absent (past)
    }
    cells.push({ day: d, label, cls });
  }
  return cells;
};

interface Props {
  month: string;                     // 'YYYY-MM'
  summary: MonthlySummary | null;
  showStats?: boolean;               // render the Present / Absent / Hours strip above the grid
}

/**
 * Month attendance calendar shared by the admin Salary tab and the employee My Attendance
 * page. Renders a Monday-aligned grid with a P / N / A letter per day plus a legend.
 */
const AttendanceCalendar: React.FC<Props> = ({ month, summary, showStats = false }) => {
  const calendar = useMemo(() => buildCalendar(month, summary), [month, summary]);

  return (
    <div>
      {showStats && summary && (
        <div className="grid grid-cols-3 border border-gray-100 rounded-lg overflow-hidden mb-4">
          <div className="p-3 border-r border-gray-100">
            <div className="text-[10px] uppercase tracking-wide text-gray-400">Present</div>
            <div className="text-xl font-bold text-green-600">{summary.presentDays}</div>
          </div>
          <div className="p-3 border-r border-gray-100">
            <div className="text-[10px] uppercase tracking-wide text-gray-400">Absent</div>
            <div className="text-xl font-bold text-red-500">{summary.absentDays}</div>
          </div>
          <div className="p-3">
            <div className="text-[10px] uppercase tracking-wide text-gray-400 flex items-center gap-1"><Clock className="h-3 w-3" /> Hours</div>
            <div className="text-xl font-bold text-gray-800">{summary.workingHours ?? 0}</div>
          </div>
        </div>
      )}

      <div className="grid grid-cols-7 gap-1">
        {WEEK.map((w) => (
          <div key={w} className="text-[9px] text-gray-400 text-center pb-1">{w}</div>
        ))}
        {calendar.map((c, i) => c === null ? (
          <div key={`b${i}`} />
        ) : (
          <div key={c.day} className={`aspect-square rounded border flex flex-col items-center justify-center ${c.cls}`}>
            <span className="text-[11px] leading-none">{c.day}</span>
            {c.label && <span className="text-[8px] font-semibold leading-none mt-0.5">{c.label}</span>}
          </div>
        ))}
      </div>

      {/* Legend — what the day-cell letters mean */}
      <div className="flex flex-wrap items-center gap-x-4 gap-y-1.5 mt-3 pt-3 border-t border-gray-100">
        <span className="inline-flex items-center gap-1.5 text-[11px] text-gray-600">
          <span className="inline-flex items-center justify-center w-4 h-4 rounded border bg-blue-50 border-blue-200 text-blue-700 text-[8px] font-semibold">P</span>
          Present (day shift)
        </span>
        <span className="inline-flex items-center gap-1.5 text-[11px] text-gray-600">
          <span className="inline-flex items-center justify-center w-4 h-4 rounded border bg-indigo-50 border-indigo-200 text-indigo-700 text-[8px] font-semibold">N</span>
          Night shift
        </span>
        <span className="inline-flex items-center gap-1.5 text-[11px] text-gray-600">
          <span className="inline-flex items-center justify-center w-4 h-4 rounded border bg-white border-gray-200 text-gray-400 text-[8px] font-semibold">A</span>
          Absent
        </span>
      </div>
    </div>
  );
};

export default AttendanceCalendar;
